import { cloneDeep } from "lodash";
import { connect } from "react-redux";
import { Form } from "formik";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import * as yup from "yup";

import { patientRequests } from "@/api/(requests)/patient";
import { sharedRequests } from "@/api/(requests)/shared";

import Button from "../../UI/Button";
import { CustomForm } from "../../../component-library/CustomForm";
import { dobValidationSchema } from "../../../containers/Dashboard/Providers/schema/newPatientValidation";
import FormikInput from "../../UI/FormikInput";
import FormikInputFormat from "../../UI/formikMobileInput";
import { getQueryParams } from "../../../utils/filters";
import { STATES } from "../../../constants/states";
import { trackEvent } from "../../../utils/EventTrackingUtil";

import "./styles.scss";

const ReleaseOfInformation = (props) => {
  const { nextStep, patientMetaData, providerMetaData } = props;
  const patientId = getQueryParams("patientId", props.location.search);
  const [initialFormValues, setInitialFormValues] = useState(cloneDeep(initialValues));

  async function getPatient() {
    let patient;

    if (!patientMetaData) {
      const headers = {
        "X-On-Behalf-Of": patientId,
        "X-On-Behalf-Of-Entity-Type": "patient",
      };

      patient = await patientRequests.get_patient_meta_data({ headers });
    } else {
      patient = patientMetaData;
    }

    return patient;
  }

  async function getProvider() {
    let provider;

    if (!providerMetaData) {
      provider = await patientRequests.get_provider_meta_data(); // TODO: refactor to query
    } else {
      provider = providerMetaData;
    }

    return provider;
  }

  useEffect(() => {
    (async () => {
      const patient = await getPatient();

      const newFormValues = { ...initialFormValues };

      newFormValues.patient.name = patient.first_name + " " + patient.last_name;
      newFormValues.patient.address = patient.address;
      newFormValues.patient.dob = patient.dob.replaceAll("/", "");

      if (patient.pcp_id === props.profile.pcp_id) {
        const provider = await getProvider();

        newFormValues.medical_record_sources.pcp.name = provider.name;
        newFormValues.medical_record_sources.pcp.address = provider.address;
        newFormValues.medical_record_sources.pcp.phone = provider.phone_number;
        newFormValues.medical_record_sources.pcp.fax = provider.fax_number;
        newFormValues.medical_record_sources.source_type_pcp = true;
      }

      newFormValues.signature.date = moment().format("MMDDYYYY");

      setInitialFormValues(newFormValues);
    })();
  }, []);

  const submit = async (formData) => {
    try {
      const data = {
        content: { ...formData },
        file_type: "RELEASE_OF_INFORMATION_V1",
      };

      const headers = {
        "X-On-Behalf-Of": patientId,
        "X-On-Behalf-Of-Entity-Type": "patient",
      };

      trackEvent("Schedule A Therapist Consultation");
      await sharedRequests.start_file_generation({ data, headers });

      nextStep();
    } catch (e) {
      console.log(e);
      nextStep();
    }
  };

  const medicalRecordsOnChange = async (formikProps) => {
    await formikProps.setFieldValue("medical_record_types.record_type", "specific_types");
  };

  function patientInfoSection(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-lg-6">
          <FormikInput
            disabled
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Patient Name",
                placeholder: "Enter Name",
              },
              elementType: "input",
              name: "patient.name",
            }}
          />
        </div>

        <div className="col-lg-6">
          <FormikInputFormat
            disabled
            {...formikInputProps}
            formatType="MM/DD/YYYY"
            formEl={{
              elementConfig: {
                label: "Date of Birth",
                placeholder: "MM/DD/YYYY",
              },
              elementType: "input",
              name: "patient.dob",
            }}
            onChange={async (val) => {
              await formikProps.setFieldValue("patient.dob", val.value);
              formikProps.setFieldTouched("patient.dob");
            }}
            value={formikProps && formikProps.values.patient.dob}
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            disabled
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Address",
                placeholder: "Enter Address",
              },
              elementType: "input",
              name: "patient.address.street",
            }}
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            disabled
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "City",
                placeholder: "Enter City",
              },
              elementType: "input",
              name: "patient.address.city",
            }}
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            disabled
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "State",
                options: STATES,
                placeholder: "Enter State",
              },
              elementType: "select",
              name: "patient.address.state",
            }}
            inputClasses="Input--sm-dropdown"
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            disabled
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Zip",
                placeholder: "Enter Zip",
              },
              elementType: "input",
              name: "patient.address.zip_code",
            }}
          />
        </div>
      </div>
    );
  }

  function medicalRecordsSource(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="my-3 tw:font-bold">
            I, or my authorized representative, authorize aptihealth to obtain/release medical
            records from the following:
            {formikProps.errors.medical_record_sources === "Required" && (
              <span className="txt-error fs-14 tw:font-normal"> Required</span>
            )}
          </div>

          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                hideTopLabel: true,
                inputClasses: "tw:font-bold",
                options: [
                  {
                    checked: formikProps.values.medical_record_sources.source_type_pcp,
                    display: "Child’s Primary Care Provider",
                    optionClasses: "col-12 py-1",
                    value: "source_type_pcp",
                  },
                ],
                type: "checkbox",
              },
              elementType: "input",
              name: "medical_record_sources.source_type_pcp",
              onclick: async (field, option) => {
                const newValue = !formikProps.values.medical_record_sources.source_type_pcp;

                await formikProps.setFieldValue("medical_record_sources.source_type_pcp", newValue);
                formikProps.setFieldTouched("medical_record_sources.source_type_pcp");
                option.checked = newValue;
              },
            }}
          />
        </div>

        <div className="ml-4 mr-2 row">
          <div className="col-12">
            <FormikInput
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  hideTopLabel: true,
                  placeholder: "Enter Primary Care Physician",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.name",
              }}
            />
          </div>

          <div className="col-lg-6">
            <FormikInput
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "Address",
                  placeholder: "Enter Address",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.address.street",
              }}
            />
          </div>

          <div className="col-lg-6">
            <FormikInput
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "City",
                  placeholder: "Enter City",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.address.city",
              }}
            />
          </div>

          <div className="col-lg-6">
            <FormikInput
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "State",
                  options: STATES,
                  placeholder: "Enter State",
                },
                elementType: "select",
                name: "medical_record_sources.pcp.address.state",
              }}
              inputClasses="Input--sm-dropdown"
            />
          </div>

          <div className="col-lg-6">
            <FormikInput
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "Zip",
                  placeholder: "Enter Zip",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.address.zip_code",
              }}
            />
          </div>

          <div className="col-lg-6">
            <FormikInputFormat
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "Phone",
                  placeholder: "Enter Phone",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.phone",
              }}
              onChange={async (val) => {
                await formikProps.setFieldValue("medical_record_sources.pcp.phone", val.value);
                formikProps.setFieldTouched("medical_record_sources.pcp.phone");
              }}
              value={formikProps && formikProps.values.medical_record_sources.pcp.phone}
            />
          </div>

          <div className="col-lg-6">
            <FormikInputFormat
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  label: "Fax",
                  placeholder: "Enter Fax",
                },
                elementType: "input",
                name: "medical_record_sources.pcp.fax",
              }}
              onChange={async (val) => {
                await formikProps.setFieldValue("medical_record_sources.pcp.fax", val.value);
                formikProps.setFieldTouched("medical_record_sources.pcp.fax");
              }}
              value={formikProps && formikProps.values.medical_record_sources.pcp.fax}
            />
          </div>
        </div>

        <div className="col-12">
          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                hideTopLabel: true,
                inputClasses: "tw:font-bold",
                options: [
                  {
                    checked: formikProps.values.medical_record_sources.source_type_all,
                    display:
                      "Health care providers (on an ongoing basis) for the duration of treatment by aptihealth",
                    optionClasses: "col-12 py-1",
                    value: "source_type_all",
                  },
                ],
                type: "checkbox",
              },
              elementType: "input",
              name: "medical_record_sources.source_type_all",
              onclick: async (field, option) => {
                const newValue = !formikProps.values.medical_record_sources.source_type_all;

                await formikProps.setFieldValue("medical_record_sources.source_type_all", newValue);
                formikProps.setFieldTouched("medical_record_sources.source_type_all");
                option.checked = newValue;
              },
            }}
          />
        </div>
      </div>
    );
  }

  function dateRange(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="my-3 tw:font-bold">
            For treatment date(s):
            <span className="txt-rose tw:font-normal"> (select one)</span>
            {formikProps.touched.date_range &&
              formikProps.touched.date_range.range_type &&
              formikProps.errors.date_range &&
              formikProps.errors.date_range.range_type === "Required" && (
                <span className="txt-error fs-14 tw:font-normal"> Required</span>
              )}
          </div>

          <FormikInput
            {...formikInputProps}
            errorClasses="d-none"
            formEl={{
              elementConfig: {
                checked: formikProps.values.date_range.range_type === "all",
                hideTopLabel: true,
                id: "date_range.range_type.all",
                inputClasses: "tw:font-bold",
                label: "Any/all previous treatment dates at your facility",
                value: "all",
              },
              elementType: "radio-single",
              name: "date_range.range_type",
              onchange: async (source_type, val) => {
                await formikProps.setFieldValue(source_type, val);
                formikProps.setFieldTouched(source_type);
              },
            }}
          />
        </div>

        <div className="col-12">
          <div className="d-flex">
            <FormikInput
              {...formikInputProps}
              errorClasses="d-none"
              formEl={{
                elementConfig: {
                  checked: formikProps.values.date_range.range_type === "specific_range",
                  hideTopLabel: true,
                  id: "date_range.range_type.specific_range",
                  label: "",
                  value: "specific_range",
                },
                elementType: "radio-single",
                name: "date_range.range_type",
                onchange: async (source_type, val) => {
                  await formikProps.setFieldValue(source_type, val);
                  formikProps.setFieldTouched(source_type);
                },
              }}
            />

            <FormikInputFormat
              {...formikInputProps}
              formatType="MM/DD/YYYY"
              formEl={{
                elementConfig: {
                  hideTopLabel: true,
                  placeholder: "MM/DD/YYYY",
                },
                elementType: "input",
                name: "date_range.specific_range.start_date",
              }}
              onChange={async (val) => {
                await formikProps.setFieldValue("date_range.specific_range.start_date", val.value);
                formikProps.setFieldTouched("date_range.specific_range.start_date");
              }}
              value={formikProps && formikProps.values.date_range.specific_range.start_date}
            />

            <div className="mt-2 mx-md-3 mx-1 tw:font-bold">To</div>

            <FormikInputFormat
              formatType="MM/DD/YYYY"
              {...formikInputProps}
              formEl={{
                elementConfig: {
                  hideTopLabel: true,
                  placeholder: "MM/DD/YYYY",
                },
                elementType: "input",
                name: "date_range.specific_range.end_date",
              }}
              onChange={async (val) => {
                await formikProps.setFieldValue("date_range.specific_range.end_date", val.value);
                formikProps.setFieldTouched("date_range.specific_range.end_date");
              }}
              value={formikProps && formikProps.values.date_range.specific_range.end_date}
            />
          </div>
        </div>
      </div>
    );
  }

  function medicalRecordTypes(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="my-3 tw:font-bold">
            Medical records includes:
            <span className="txt-rose tw:font-normal"> (select one)</span>
            {formikProps.touched.medical_record_types &&
              formikProps.touched.medical_record_types.record_type &&
              formikProps.errors.medical_record_types &&
              formikProps.errors.medical_record_types.record_type === "Required" && (
                <span className="txt-error fs-14 tw:font-normal"> Required</span>
              )}
          </div>

          <FormikInput
            {...formikInputProps}
            errorClasses="d-none"
            formEl={{
              elementConfig: {
                checked: formikProps.values.medical_record_types.record_type === "all",
                hideTopLabel: true,
                id: "medical_record_types.record_type.all",
                inputClasses: "tw:font-bold",
                label:
                  "All records regarding my treatment, hospitalization, and outpatient care. A separate authorization is required for the use or disclosure of psychotherapy notes or research health information.",
                value: "all",
              },
              elementType: "radio-single",
              name: "medical_record_types.record_type",
              onchange: async (source_type, val) => {
                await formikProps.setFieldValue("medical_record_types.specific_types", {
                  admission_summary: false,
                  discharge_summary: false,
                  drug_treatment: false,
                  history_and_physical: false,
                  iep: false,
                  lab_reports: false,
                  medication_history: false,
                  other: false,
                  other_text: "",
                  progress_notes: false,
                  psychological_testing: false,
                });
                await formikProps.setFieldValue(source_type, val);
                formikProps.setFieldTouched(source_type);
              },
            }}
          />
        </div>

        <div className="col-12">
          <FormikInput
            {...formikInputProps}
            errorClasses="d-none"
            formEl={{
              elementConfig: {
                checked: formikProps.values.medical_record_types.record_type === "specific_types",
                hideTopLabel: true,
                id: "medical_record_types.record_type.specific_types",
                inputClasses: "tw:font-bold",
                label: (
                  <>
                    The following records, specific types of health information, or records for the
                    date(s) of treatment as specified:
                    {formikProps.touched.medical_record_types &&
                      formikProps.touched.medical_record_types.specific_types &&
                      formikProps.errors.medical_record_types &&
                      formikProps.errors.medical_record_types.specific_types === "Required" && (
                        <span className="txt-error fs-14 tw:font-normal"> Required</span>
                      )}
                  </>
                ),
                value: "specific_types",
              },
              elementType: "radio-single",
              name: "medical_record_types.record_type",
              onchange: async (source_type, val) => {
                await formikProps.setFieldValue(source_type, val);
                formikProps.setFieldTouched(source_type);
              },
            }}
          />
        </div>

        <div className="row ml-5 mr-3">
          <div className="col-12">
            <FormikInput
              {...formikInputProps}
              errorClasses="d-none"
              formEl={{
                elementClasses: "col-12",
                elementConfig: {
                  hideTopLabel: true,
                  optionClasses: "col-lg-4 col-12 py-1",
                  options: [
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.admission_summary,
                      display: "Admission Summary",
                      value: "admission_summary",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.drug_treatment,
                      display: "Drug Treatment",
                      value: "drug_treatment",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.medication_history,
                      display: "Medication History",
                      value: "medication_history",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.discharge_summary,
                      display: "Discharge Summary",
                      value: "discharge_summary",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.history_and_physical,
                      display: "History and Physical",
                      value: "history_and_physical",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types.progress_notes,
                      display: "Progress Notes",
                      value: "progress_notes",
                    },
                    {
                      checked:
                        formikProps.values.medical_record_types.specific_types
                          .psychological_testing,
                      display: "Psychological Testing",
                      value: "psychological_testing",
                    },
                    {
                      checked: formikProps.values.medical_record_types.specific_types.lab_reports,
                      display: "Lab Reports",
                      value: "lab_reports",
                    },
                    {
                      checked: formikProps.values.medical_record_types.specific_types.iep,
                      display: "IEP",
                      value: "iep",
                    },
                    {
                      checked: formikProps.values.medical_record_types.specific_types.other,
                      display: "Other",
                      onChange: async (e) => {
                        const val = e.target.value;

                        await formikProps.setFieldValue(
                          "medical_record_types.specific_types.other_text",
                          val,
                        );
                      },
                      openText: true,
                      optionClasses: "col-12 py-1",
                      value: "other",
                    },
                  ],
                  type: "checkbox",
                },
                elementType: "input",
                errorClasses: "InputError--lg-2",
                name: "medical_record_types.specific_types",
                onclick: async (field, option) => {
                  await medicalRecordsOnChange(formikProps);
                  const newValue =
                    !formikProps.values.medical_record_types.specific_types[option.value];

                  await formikProps.setFieldValue(`${field}.${option.value}`, newValue);
                  formikProps.setFieldTouched(`${field}.${option.value}`);
                  option.checked = newValue;
                },
              }}
            />
          </div>

          <div className="mb-4 tw:font-bold">
            I expressly consent to the disclosure of
            <span>
              {" "}
              <u>all of my medical records indicated above.</u>
            </span>
          </div>
        </div>
      </div>
    );
  }

  function purpose(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="my-3 tw:font-bold">
            Purpose: The purpose and limitations (if any) of the requested use or disclosure is:
            <span className="txt-rose tw:font-normal"> (select one)</span>
            {formikProps.touched.purpose &&
              formikProps.touched.purpose.purpose_type &&
              formikProps.errors.purpose &&
              formikProps.errors.purpose.purpose_type === "Required" && (
                <span className="txt-error fs-14 tw:font-normal"> Required</span>
              )}
          </div>

          <FormikInput
            {...formikInputProps}
            errorClasses="d-none"
            formEl={{
              elementConfig: {
                checked: formikProps.values.purpose.purpose_type === "all",
                hideTopLabel: true,
                id: "purpose.purpose_type.all",
                inputClasses: "tw:font-bold",
                label: "For treatment purposes and/or collaboration of care; OR",
                value: "all",
              },
              elementType: "radio-single",
              name: "purpose.purpose_type",
              onchange: async (source_type, val) => {
                await formikProps.setFieldValue(source_type, val);
                formikProps.setFieldTouched(source_type);
              },
            }}
          />
        </div>

        <div className="col-12">
          <FormikInput
            {...formikInputProps}
            errorClasses="d-none"
            formEl={{
              elementConfig: {
                checked: formikProps.values.purpose.purpose_type === "other",
                hideTopLabel: true,
                id: "purpose.purpose_type.other",
                label: "Other",
                onChange: async (e) => {
                  const val = e.target.value;

                  await formikProps.setFieldValue("purpose.other_text", val);
                },
                openText: true,
                value: "other",
              },
              elementType: "radio-single",
              name: "purpose.purpose_type",
              onchange: async (source_type, val) => {
                await formikProps.setFieldValue(source_type, val);
                formikProps.setFieldTouched(source_type);
              },
            }}
          />
        </div>
      </div>
    );
  }

  function disclaimer() {
    return (
      <div className="row my-4">
        <div className="col-12">
          <div className="mb-2 tw:font-bold">
            This authorization ends <u>one year from the date of signature below.</u>
          </div>

          <div className="mb-2 tw:font-bold">
            In accordance with applicable state law and the Health Insurance Portability and
            Accountability Act of 1996 (HIPAA) Privacy Standards, I understand and agree that:
          </div>

          <div className="mb-2">
            This authorization may include information relating to alcohol and drug abuse, mental
            health treatment, psychotherapy notes) and confidential HIV related information. In the
            event the health information described above includes any of these types of information,
            I specifically authorize the release of such information to the recipient identified
            above. I have the right to revoke this authorization, in writing, at any time, except
            where action has been taken based upon my authorization. To revoke this authorization, I
            must do so in writing and send it to the appropriate disclosing party.
          </div>

          <div className="mb-2">
            It is possible that information used or disclosed with my permission may be re-disclosed
            by the recipient and is no longer protected by the HIPAA Privacy Standards or state law.
            If I am authorizing the release of alcohol or drug treatment, mental health treatment,
            psychotherapy notes, or HIV related information, the recipient is prohibited from
            re­disclosing such information or using the disclosed information for any other purpose
            without my authorization, unless permitted to do so under federal or state law.
          </div>

          <div className="mb-2">
            Signing this authorization is voluntary. My treatment by any party, payment, enrollment
            in a health plan, or eligibility for benefits may not be conditioned upon my signing of
            this authorization (unless treatment is sought only to create health information for a
            third party or to take part in a research study).
          </div>
        </div>
      </div>
    );
  }

  function signature(formikInputProps, formikProps) {
    return (
      <div className="row">
        <div className="col-lg-10">
          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: (
                  <>
                    Patient/Representative Signature{" "}
                    <span className="txt-rose tw:font-normal">(required)</span>
                  </>
                ),
                placeholder: "Signature",
              },
              elementType: "input",
              name: "signature.representative_signature",
            }}
          />
        </div>

        <div className="col-lg-2">
          <FormikInputFormat
            disabled
            {...formikInputProps}
            formatType="MM/DD/YYYY"
            formEl={{
              elementConfig: {
                label: (
                  <>
                    Date <span className="txt-rose tw:font-normal">(required)</span>
                  </>
                ),
                placeholder: "MM/DD/YYYY",
              },
              elementType: "input",
              name: "signature.date",
            }}
            onChange={async (val) => {
              await formikProps.setFieldValue("signature.date", val.value);
              formikProps.setFieldTouched("signature.date");
            }}
            value={formikProps && formikProps.values.signature.date}
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: (
                  <>
                    If not patient, name of person signing{" "}
                    <span className="txt-rose tw:font-normal">(required)</span>
                  </>
                ),
                placeholder: "Enter Name",
              },
              elementType: "input",
              name: "signature.name_of_person_signing",
            }}
          />
        </div>

        <div className="col-lg-6">
          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: (
                  <>
                    Relationship or authority to sign for patient{" "}
                    <span className="txt-rose tw:font-normal">(required)</span>
                  </>
                ),
                placeholder: "Enter Relationship",
              },
              elementType: "input",
              name: "signature.relationship_to_sign_for_patient",
            }}
          />
        </div>
      </div>
    );
  }

  const renderForm = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return (
      <Form>
        <div className="fs-18 mb-4 tw:font-bold">
          HIPAA Authorization for Use or Disclosure of Health Information
        </div>

        <div className="mb-3">{patientInfoSection(formikInputProps, formikProps)}</div>

        <div className="mb-3">{medicalRecordsSource(formikInputProps, formikProps)}</div>

        <div className="mb-3">{dateRange(formikInputProps, formikProps)}</div>

        <div className="mb-3">{medicalRecordTypes(formikInputProps, formikProps)}</div>

        <div className="mb-3">{purpose(formikInputProps, formikProps)}</div>

        <div className="mb-3">{disclaimer()}</div>

        <div className="mb-3">{signature(formikInputProps, formikProps)}</div>

        <div className="d-flex justify-content-end pb-4">
          <Button className="Btn Btn--pri" type="submit">
            Continue
          </Button>
        </div>
      </Form>
    );
  };

  return (
    <>
      <div className="txt-sec pt-2 mx-3 workflow-step-title tw:font-bold">
        Step 2 of 3: Release of Information
      </div>

      <div className="mx-4 my-3">
        <CustomForm
          initialValues={initialFormValues}
          onSubmit={submit}
          render={renderForm}
          validationSchema={validation}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, {})(withRouter(ReleaseOfInformation));

const validation = yup.object().shape({
  date_range: yup.object().shape({
    range_type: yup.string().required("Required"),
    specific_range: yup.object().when("range_type", {
      is: (actualValue) => actualValue === "specific_range",
      then: yup.object().shape({
        end_date: dobValidationSchema.required("Required"),
        start_date: dobValidationSchema.required("Required"),
      }),
    }),
  }),
  medical_record_sources: yup
    .object()
    .test(
      "check_at_least_one_true",
      "Required",
      (obj) => obj.source_type_all || obj.source_type_pcp,
    )
    .shape({
      pcp: yup.object().when("source_type_pcp", {
        is: (actualValue) => actualValue,
        then: yup.object().shape({
          address: yup.object().shape({
            city: yup.string().required("Required"),
            state: yup.string().required("Required"),
            street: yup.string().required("Required"),
            zip_code: yup.string().required("Required"),
          }),
          fax: yup.string().required("Required"),
          name: yup.string().required("Required"),
          phone: yup.string().required("Required"),
        }),
      }),
    }),
  medical_record_types: yup.object().shape({
    record_type: yup.string().required("Required"),
    specific_types: yup.object().when("record_type", {
      is: (actualValue) => actualValue === "specific_types",
      then: yup.object().test("check_at_least_one_true", "Required", (obj) => {
        return Object.values(obj).some((isChecked) => isChecked);
      }),
    }),
  }),
  patient: yup.object().shape({
    address: yup.object().shape({
      city: yup.string().required("Required"),
      state: yup.string().required("Required"),
      street: yup.string().required("Required"),
      zip_code: yup.string().required("Required"),
    }),
    dob: dobValidationSchema.required("Required"),
    name: yup.string().required("Required"),
  }),
  purpose: yup.object().shape({
    purpose_type: yup.string().required("Required"),
  }),
  signature: yup.object().shape({
    date: yup.string().required("Required"),
    name_of_person_signing: yup.string().required("Required"),
    relationship_to_sign_for_patient: yup.string().required("Required"),
    representative_signature: yup.string().required("Required"),
  }),
});

const initialValues = {
  date_range: {
    range_type: "", // specific_range || all
    specific_range: {
      end_date: "",
      start_date: "",
    },
  },
  medical_record_sources: {
    pcp: {
      address: {
        city: "",
        state: "",
        street: "",
        zip_code: "",
      },
      fax: "",
      name: "",
      phone: "",
    },
    source_type_all: true,
    source_type_pcp: false,
  },
  medical_record_types: {
    record_type: "", // specific_types || all
    specific_types: {
      admission_summary: false,
      discharge_summary: false,
      drug_treatment: false,
      history_and_physical: false,
      iep: false,
      lab_reports: false,
      medication_history: false,
      other: false,
      other_text: "",
      progress_notes: false,
      psychological_testing: false,
    },
  },
  patient: {
    address: {
      city: "",
      state: "",
      street: "",
      zip_code: "",
    },
    dob: "",
    name: "",
  },
  purpose: {
    other_text: "",
    purpose_type: "", // all || other
  },
  signature: {
    date: "",
    name_of_person_signing: "",
    relationship_to_sign_for_patient: "",
    representative_signature: "",
  },
};
